import { ExportFormat, type ExportOptions } from '@/core/export/types'
import { assertJpegQuality } from '@/core/export/validate'
import { EDITED_WITH, injectJpegMetadata, injectPngMetadata } from '@/core/render/encoders/metadata'
import type { ImageEncoder } from '@/core/render/encoders/types'
import { EncodeFailedError } from '@/core/export/errors'

export function validateEncoderOptions(options: ExportOptions): void {
  if (!Object.values(ExportFormat).includes(options.format)) {
    throw new Error(`Cannot encode unknown export format: "${String(options.format)}"`)
  }
  assertJpegQuality(options.jpegQuality)
}

function createCanvasEncoder(
  format: ExportFormat,
  mimeType: string,
  extension: string,
): ImageEncoder {
  return {
    format,
    mimeType,
    extension,
    async encode(canvas, options) {
      validateEncoderOptions(options)
      if (options.format !== format) {
        throw new Error(`Encoder configuration mismatch: ${format} encoder received ${options.format} options`)
      }
      const blob = await canvas.convertToBlob({
        type: mimeType,
        ...(format === ExportFormat.Jpeg ? { quality: options.jpegQuality } : {}),
      })
      assertEncodedBlob(blob)
      const bytes = new Uint8Array(await blob.arrayBuffer())
      const withMetadata = format === ExportFormat.Jpeg
        ? injectJpegMetadata(bytes, EDITED_WITH)
        : injectPngMetadata(bytes, EDITED_WITH)
      const output = new Uint8Array(withMetadata.length)
      output.set(withMetadata)
      return new Blob([output.buffer], { type: mimeType })
    },
  }
}

export function assertEncodedBlob(blob: Blob | null): asserts blob is Blob {
  if (!blob || blob.size === 0) throw new EncodeFailedError()
}

export const JPEG_ENCODER = createCanvasEncoder(ExportFormat.Jpeg, 'image/jpeg', 'jpg')
export const PNG_ENCODER = createCanvasEncoder(ExportFormat.Png, 'image/png', 'png')
